import moment from 'moment';

/**
 * Shared logic for the Summary & Goals report.
 *
 * There are two Company/Report screens that both build this report — the admin
 * one (`page/companyReport/companyReport.jsx`, route `/company/report`) and the
 * company-facing one (`page/wellnessProgram/companyReport.jsx`, route
 * `/Company/Report`). They used to carry independent copies of the goal maths,
 * which is exactly how they drifted apart. Keep the rules here so a change lands
 * on both screens at once.
 */

/**
 * ZONE NAMING — the database field names are shifted one zone below the labels
 * users actually see. This is intentional (Fat Burn *is* moderate intensity,
 * Cardio/Peak *are* vigorous), and it is the single easiest thing to get wrong
 * here. The employee-facing mapping is declared in `page/summaryGoals.jsx`
 * (see its comments at "front End Moderate = backend Light") and its chart
 * series; this module is the authority for reporting.
 *
 *   Fitbit zone name │ DB heartZoneName │ label shown to people
 *   ─────────────────┼──────────────────┼──────────────────────
 *   Fat Burn         │ light            │ Moderate
 *   Cardio           │ moderate         │ Vigorous
 *   Peak             │ vigorous         │ Peak
 *
 * Garmin writes the same lowercase DB vocabulary; Apple is stored unnormalised
 * exactly as the iOS app posts it and is read through the Garmin branch, so it
 * is expected to use the same lowercase names.
 */
export const ZONE_LABELS = {
    moderate: 'Moderate',  // DB `light`    / Fitbit "Fat Burn"
    vigorous: 'Vigorous',  // DB `moderate` / Fitbit "Cardio"
    peak: 'Peak',          // DB `vigorous` / Fitbit "Peak"
};

export const ZONE_GLOSSARY =
    'Moderate = Fat Burn zone, Vigorous = Cardio zone, Peak = Peak zone';

// Goal targets, mirroring page/summaryGoals.jsx.
export const GOAL_TARGET_HIGH = 600;
export const GOAL_TARGET_LOW = 300;

export const CATEGORY_ACHIEVED = 'Goal Achievers';
export const CATEGORY_SHORTFALL = 'Qualified Activity - Goal Not Reached';

/**
 * Which goal rule applies to a member, matching how summaryGoals.jsx branches on
 * GET /user/device/status:
 *   isFitbitConnected = !!fitBitAccessToken
 *   isGarminConnected = !!garminId
 *   isAppleConnected  = !!isAppleConnected
 * and then `if (fitConnect && !garConnect && !appleConnected)` for the Fitbit
 * Active Zone Minutes rule, else the raw-minutes rule.
 */
export const deviceContextFor = (user) => {
    const fitbit = Boolean(user?.fitBitAccessToken || user?.fitBitId);
    const garmin = Boolean(user?.garminId);
    const garminToken = Boolean(user?.garminAuthToken);
    const apple = Boolean(user?.isAppleConnected);

    return {
        fitbit,
        garmin,
        apple,
        isConnected: fitbit || garmin || garminToken || apple,
        // The AZM rule applies only when Fitbit is the sole connected source.
        useAzmRule: fitbit && !garmin && !apple,
        label: fitbit && !garmin && !apple
            ? 'Fitbit'
            : [garmin || garminToken ? 'Garmin' : null, apple ? 'Apple' : null, fitbit ? 'Fitbit' : null]
                .filter(Boolean).join(' + ') || 'None',
    };
};

/**
 * Replicates the goal maths an employee sees on their own Summary & Goals page,
 * so the employer report cannot disagree with it.
 *
 * Fitbit only — Active Zone Minutes: Fitbit awards 1 AZM per Moderate minute and
 * 2 AZM per Vigorous/Peak minute, against a flat 600 AZM target. When a member
 * recorded no Vigorous or Peak minutes at all, only raw Moderate minutes count.
 *
 * Garmin / Apple — raw minutes, all three zones summed, against 300 if the
 * member logged any Vigorous minutes and 600 otherwise.
 *
 * The goal is met at >= target (the employee page shows "100% of goal, 0 to go"
 * on equality).
 */
export const computeGoal = ({ moderate, vigorous, peak, useAzmRule }) => {
    // summaryGoals.jsx rounds each zone before applying the rule.
    const mod = Math.round(num(moderate));
    const vig = Math.round(num(vigorous));
    const pk = Math.round(num(peak));

    let target;
    let counted;
    let basis;

    if (useAzmRule) {
        target = GOAL_TARGET_HIGH;
        counted = (vig > 0 || pk > 0) ? mod + (vig * 2) + (pk * 2) : mod;
        basis = 'Active Zone Minutes (Moderate x1, Vigorous x2, Peak x2)';
    } else {
        counted = mod + vig + pk;
        target = vig === 0 ? GOAL_TARGET_HIGH : GOAL_TARGET_LOW;
        basis = 'Raw minutes (Moderate + Vigorous + Peak)';
    }

    const remaining = target - counted;

    return {
        target,
        counted,
        basis,
        goalMet: counted >= target,
        minutesOverGoal: remaining < 0 ? Math.abs(remaining) : 0,
        minutesStillNeeded: remaining > 0 ? remaining : 0,
        // Same percentage the employee page renders.
        goalProgress: remaining >= 0
            ? 100 - Math.round((remaining / target) * 100)
            : 100 + Math.round(Math.abs((remaining / target) * 100)),
    };
};

// Zone totals are summed from two different device payload shapes; a missing
// zone would otherwise propagate NaN all the way into the employer's report.
export const num = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

/**
 * Reads a value written through authStorage that holds an object.
 *
 * Callers historically wrapped their own JSON.stringify before calling
 * authStorage.setItem, so the stored value is a JSON string that authStorage
 * already parses back into an object on read. Calling JSON.parse on that result
 * throws, which is how the Billing card silently ended up empty. This tolerates
 * both shapes.
 */
export const readStoredObject = (storedItem) => {
    if (!storedItem) return {};
    if (typeof storedItem === 'object') return storedItem;
    try {
        return JSON.parse(storedItem);
    } catch {
        return {};
    }
};

/**
 * Sums heart-rate-zone minutes and calories for one member over the period.
 *
 * Fitbit returns the four zones positionally — [0] Out of Range, [1] Fat Burn,
 * [2] Cardio, [3] Peak. Garmin and Apple instead return named zones
 * (`heartZoneName`), already grouped per day by their DAOs, and share a shape —
 * so the second argument takes either.
 */
export const aggregateZoneTotals = (fitbitData, namedZoneData) => {
    const tempCaloriesBurned = { light: 0, moderate: 0, vigorous: 0 };
    const tempTimeConsumed = { light: 0, moderate: 0, vigorous: 0 };
    // Days that produced minutes in ANY counting zone. Moderate-only members do
    // reach their goal under both rules, so counting only the top two zones here
    // would report them as having zero active days.
    let activeDays = 0;

    fitbitData?.forEach((entry) => {
        const zones = entry?.heartRateZones || [];
        const light = num(zones[1]?.minutes);
        const moderate = num(zones[2]?.minutes);
        const vigorous = num(zones[3]?.minutes);

        tempCaloriesBurned.light += num(zones[1]?.caloriesOut);
        tempCaloriesBurned.moderate += num(zones[2]?.caloriesOut);
        tempCaloriesBurned.vigorous += num(zones[3]?.caloriesOut);
        tempTimeConsumed.light += light;
        tempTimeConsumed.moderate += moderate;
        tempTimeConsumed.vigorous += vigorous;

        if (light + moderate + vigorous > 0) activeDays++;
    });

    namedZoneData?.forEach((entry) => {
        const timeSpend = {};
        const calorieConsumed = {};
        entry?.heartRateZones?.forEach((zone) => {
            // Zone names are lower-cased because real Fitbit rows carry two
            // different casings ("light" and "Light"); Garmin and Apple use
            // lower case, but normalising costs nothing and prevents a silent miss.
            const key = String(zone?.heartZoneName || '').toLowerCase().trim();
            // Garmin reports fractional minutes; summaryGoals.jsx ceilings each
            // day's zone before summing, so this must too or the two views
            // disagree for Garmin/Apple members.
            timeSpend[key] = Math.ceil(num(zone?.minutes));
            calorieConsumed[key] = num(zone?.caloriesOut);
        });

        const light = num(timeSpend?.light);
        const moderate = num(timeSpend?.moderate);
        const vigorous = num(timeSpend?.vigorous);

        tempCaloriesBurned.light += num(calorieConsumed?.light);
        tempCaloriesBurned.moderate += num(calorieConsumed?.moderate);
        tempCaloriesBurned.vigorous += num(calorieConsumed?.vigorous);
        tempTimeConsumed.light += light;
        tempTimeConsumed.moderate += moderate;
        tempTimeConsumed.vigorous += vigorous;

        if (light + moderate + vigorous > 0) activeDays++;
    });

    return { tempCaloriesBurned, tempTimeConsumed, activeDays };
};

/**
 * Turns one company member plus their period totals into a report row.
 *
 * Note the rename across the boundary: the DB's light/moderate/vigorous become
 * the reporting vocabulary moderate/vigorous/peak. Everything downstream of this
 * function speaks the labels users see.
 */
export const buildParticipantRecord = (user, activityRecord) => {
    const { vigorous, moderate, light } = activityRecord?.tempTimeConsumed || {
        vigorous: 0, moderate: 0, light: 0,
    };
    const calories = activityRecord?.tempCaloriesBurned;

    const moderateMinutes = Math.round(num(light));
    const vigorousMinutes = Math.round(num(moderate));
    const peakMinutes = Math.round(num(vigorous));

    const device = deviceContextFor(user);
    const goal = computeGoal({
        moderate: moderateMinutes,
        vigorous: vigorousMinutes,
        peak: peakMinutes,
        useAzmRule: device.useAzmRule,
    });

    return {
        user,
        name: [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || '-',
        email: user?.email || '-',
        device,
        isConnected: device.isConnected,
        moderateMinutes,
        vigorousMinutes,
        peakMinutes,
        // Minutes that actually counted toward the goal under the member's rule.
        countedMinutes: goal.counted,
        goalTarget: goal.target,
        goalBasis: goal.basis,
        achievedGoal: goal.goalMet,
        goalProgress: goal.goalProgress,
        minutesOverGoal: goal.minutesOverGoal,
        minutesStillNeeded: goal.minutesStillNeeded,
        // "Qualified activity" = the member produced minutes that count toward
        // their goal. Only members who produced none are excluded from the
        // detail table.
        hasQualifiedActivity: goal.counted > 0,
        caloriesBurned: Math.round(
            num(calories?.light) + num(calories?.moderate) + num(calories?.vigorous)
        ),
        activeDays: activityRecord?.activeDays || 0,
    };
};

/** Splits participants into the reporting categories, in report order. */
export const categoriseParticipants = (participants = []) => {
    // Ranked by progress against their own goal, not raw minutes — members can
    // be held to different targets, so raw minutes would rank them unfairly.
    const achievers = participants
        .filter((r) => r.achievedGoal)
        .sort((a, b) => b.goalProgress - a.goalProgress);

    const shortfall = participants
        .filter((r) => !r.achievedGoal && r.hasQualifiedActivity)
        .sort((a, b) => b.goalProgress - a.goalProgress);

    // The only members excluded from the detail table: connected, but produced
    // no qualified minutes at all.
    const noActivity = participants.filter((r) => !r.hasQualifiedActivity && r.isConnected);
    const notConnected = participants.filter((r) => !r.isConnected);

    return {
        achievers,
        shortfall,
        noActivity,
        notConnected,
        connectedCount: participants.filter((r) => r.isConnected).length,
        reported: [
            ...achievers.map((r) => ({ ...r, category: CATEGORY_ACHIEVED })),
            ...shortfall.map((r) => ({ ...r, category: CATEGORY_SHORTFALL })),
        ],
    };
};

/**
 * Builds the Summary & Goals report as a sheet: a header block, a reconciling
 * summary, a device breakdown, then the participant detail with Goal Achievers
 * first and the qualified-but-short group directly after.
 *
 * `category` optionally narrows the PARTICIPANT DETAIL to one category —
 * CATEGORY_ACHIEVED or CATEGORY_SHORTFALL — for the per-category downloads.
 * The SUMMARY and DEVICE BREAKDOWN blocks always describe the whole connected
 * population, so a single-category file still reconciles and the reader can see
 * the denominator rather than mistaking the subset for the total.
 *
 * Returns `{ rows, boldRows, boldLabelRows }`. The row indexes are tracked as the
 * sheet is assembled — the blocks are conditional, so counting them afterwards
 * would drift the moment a block is added or removed.
 *
 * Percentages are emitted as plain numbers with the unit in the column heading,
 * so they stay sortable in Excel rather than becoming text like "107%".
 */
export const buildSummaryGoalsRows = ({ companyName, participants, category }) => {
    const { achievers, shortfall, noActivity, notConnected, connectedCount, reported } =
        categoriseParticipants(participants);

    const pct = (n) => (connectedCount > 0 ? Math.round((n / connectedCount) * 100) : 0);
    const periodStart = moment().startOf('month');
    const periodEnd = moment().endOf('month');

    const rows = [];
    const boldRows = [];
    const boldLabelRows = [];

    /** Appends a row. `bold` bolds the whole row, `label` bolds only column A. */
    const push = (row = [], { bold = false, label = false } = {}) => {
        if (bold) boldRows.push(rows.length);
        if (label) boldLabelRows.push(rows.length);
        rows.push(row);
    };

    push([category ? `${category} Report` : 'Summary & Goals Report'], { bold: true });
    push(['Company', companyName || '-'], { label: true });
    push(['Reporting Period', `${periodStart.format('DD MMM YYYY')} to ${periodEnd.format('DD MMM YYYY')}`], { label: true });
    push(['Report Generated', moment().format('DD MMM YYYY, hh:mm A')], { label: true });
    push();

    if (category) {
        push([
            'Report Covers',
            category === CATEGORY_ACHIEVED
                ? 'Members who reached their monthly goal. Counts for every category are in the summary below.'
                : 'Members who produced qualifying activity but did not reach their monthly goal. Counts for every category are in the summary below.',
        ], { label: true });
    }
    push(['Heart-Rate Zones', ZONE_GLOSSARY], { label: true });
    push(['Goal Minutes', 'Minutes that count toward the goal, using the same rule the member sees on their own Summary & Goals page'], { label: true });
    push(['Fitbit Goal', `${GOAL_TARGET_HIGH} Active Zone Minutes (Moderate counts once, Vigorous and Peak count double)`], { label: true });
    push(['Garmin / Apple Goal', `${GOAL_TARGET_LOW} minutes with any Vigorous activity, otherwise ${GOAL_TARGET_HIGH}`], { label: true });
    push();

    push(['SUMMARY'], { bold: true });
    push(['Category', 'Participants', '% of Connected'], { bold: true });
    push([CATEGORY_ACHIEVED, achievers.length, pct(achievers.length)]);
    push([CATEGORY_SHORTFALL, shortfall.length, pct(shortfall.length)]);
    push(['Total Reported', reported.length, pct(reported.length)]);
    push(['Excluded - No Qualifying Activity', noActivity.length, pct(noActivity.length)]);
    push(['Total Connected Participants', connectedCount, pct(connectedCount)]);

    // Only surfaced when it applies, so the common case stays uncluttered.
    if (notConnected.length > 0) {
        push(['No Device Connected', notConnected.length, '-']);
    }

    // Device breakdown. Grouped by the label a member is reported under, and
    // split by outcome so the employer can see, for example, that every Apple
    // member fell short. Members with no qualifying activity are counted here
    // even though they are absent from the detail table below — otherwise the
    // device totals would not reconcile with the participant totals.
    const byDevice = new Map();
    participants.filter((r) => r.isConnected).forEach((r) => {
        const key = r.device?.label || 'Unknown';
        const bucket = byDevice.get(key) || { total: 0, achieved: 0, short: 0, noActivity: 0 };
        bucket.total++;
        if (r.achievedGoal) bucket.achieved++;
        else if (r.hasQualifiedActivity) bucket.short++;
        else bucket.noActivity++;
        byDevice.set(key, bucket);
    });

    push();
    push(['DEVICE BREAKDOWN'], { bold: true });
    push([
        'Device',
        'Participants',
        '% of Connected',
        CATEGORY_ACHIEVED,
        CATEGORY_SHORTFALL,
        'No Qualifying Activity',
        'Goal Rule Applied',
    ], { bold: true });
    if (byDevice.size === 0) {
        push(['No participant has a device connected.']);
    }
    [...byDevice.entries()]
        .sort((a, b) => b[1].total - a[1].total)
        .forEach(([label, b]) => {
            push([
                label,
                b.total,
                pct(b.total),
                b.achieved,
                b.short,
                b.noActivity,
                label === 'Fitbit'
                    ? `${GOAL_TARGET_HIGH} Active Zone Minutes`
                    : `${GOAL_TARGET_LOW}/${GOAL_TARGET_HIGH} raw minutes`,
            ]);
        });

    // Narrow the detail rows when this file is for a single category.
    const detailRows = category
        ? reported.filter((r) => r.category === category)
        : reported;

    push();
    push([category ? `PARTICIPANT DETAIL - ${category}` : 'PARTICIPANT DETAIL'], { bold: true });
    push([
        'Category',
        'Rank In Category',
        'Name',
        'Email',
        'Device',
        'Monthly Goal',
        'Goal Minutes Achieved',
        'Goal Progress %',
        'Minutes Over Goal',
        'Minutes Still Needed',
        `${ZONE_LABELS.moderate} Minutes`,
        `${ZONE_LABELS.vigorous} Minutes`,
        `${ZONE_LABELS.peak} Minutes`,
        'Active Days',
        'Calories Burned',
        'Goal Basis',
    ], { bold: true });

    if (detailRows.length === 0) {
        push([
            category === CATEGORY_ACHIEVED
                ? 'No participant reached their monthly goal in this period.'
                : category === CATEGORY_SHORTFALL
                    ? 'No participant fell short of their monthly goal in this period.'
                    : 'No participant produced qualifying activity in this period.',
        ]);
    }

    let rank = 0;
    let currentCategory = '';
    detailRows.forEach((r) => {
        if (r.category !== currentCategory) {
            currentCategory = r.category;
            rank = 0;
        }
        rank++;
        push([
            r.category,
            rank,
            r.name,
            r.email,
            r.device?.label || '-',
            r.goalTarget,
            r.countedMinutes,
            r.goalProgress,
            r.achievedGoal ? r.minutesOverGoal : '',
            r.achievedGoal ? '' : r.minutesStillNeeded,
            r.moderateMinutes,
            r.vigorousMinutes,
            r.peakMinutes,
            r.activeDays,
            r.caloriesBurned,
            r.goalBasis,
        ]);
    });

    return { rows, boldRows, boldLabelRows };
};

/**
 * e.g. `Northwind-Logistics-Goal-Achievers-Jul-2026-Report.xlsx`
 * Omit `category` for the combined file (`...-Summary-and-Goals-...`).
 */
export const summaryGoalsFileName = (companyName, category) => {
    const slug = category === CATEGORY_ACHIEVED
        ? 'Goal-Achievers'
        : category === CATEGORY_SHORTFALL
            ? 'Goal-Not-Reached'
            : 'Summary-and-Goals';
    return `${(companyName || 'Company').replace(/[^\w-]+/g, '-')}` +
        `-${slug}-${moment().format('MMM-YYYY')}-Report.xlsx`;
};

/** Worksheet tab name, kept inside Excel's 31-character limit. */
export const summaryGoalsSheetName = (category) =>
    (category === CATEGORY_ACHIEVED
        ? 'Goal Achievers'
        : category === CATEGORY_SHORTFALL
            ? 'Goal Not Reached'
            : 'Summary and Goals');
